import { cmp, div, isZero, mul, quantise, sign, sub, type Money } from '@/lib/money'
import { KNOWN_TIERS, knownTierSchema, type KnownTier, type Plan } from '@/schemas/subscriptions'

/**
 * The catalogue as the pricing screens draw it: tier → period → the plan with
 * and without AI signals.
 *
 * `plans/` is a flat, server-ordered list with dev's real mess in it — two
 * Shark rows per period (with and without signals), no Sadasya yearly, ties
 * in `display_order`. Columns are the TIER, never `title`/`name`; the period
 * axis is `periodDays`, since `billing_cycle` is null on 3-month plans.
 */

/**
 * One period a tier is sold for. At least one variant always exists; a period
 * where only the with-signals plan is sold has no base.
 */
export type PeriodOffer = { periodDays: number } & (
  { base: Plan; withSignals: Plan | null } | { base: null; withSignals: Plan }
)

export interface Catalog {
  /** Shortest period first. Free has none — it is never sold. */
  tiers: Readonly<Record<KnownTier, readonly PeriodOffer[]>>
  /** Every period any tier is sold for, shortest first — the period switch. */
  periods: readonly number[]
}

/**
 * Whether web checkout would take this plan. Mirrors mm-be's refusals:
 * app-only, free, no duration set, no price set (`checkout.py`). Plus one of
 * our own: nothing on the Free TIER is sold. mm-be reads a title it does not
 * recognise as tier `free` (`types.tier_of`), so a priced row there is a
 * mis-titled paid plan whose buyer would be granted Free.
 */
export const isPurchasable = (plan: Plan): boolean =>
  plan.tier !== 'free' &&
  !plan.appOnly &&
  !plan.isFree &&
  plan.periodDays > 0 &&
  sign(plan.price.total) > 0

const byServerOrder = (a: Plan, b: Plan): number => a.displayOrder - b.displayOrder || a.id - b.id

interface Cell {
  base: Plan | null
  withSignals: Plan | null
}

const toOffers = (cells: ReadonlyMap<number, Cell>): PeriodOffer[] =>
  [...cells]
    .sort(([a], [b]) => a - b)
    .flatMap(([periodDays, { base, withSignals }]): PeriodOffer[] => {
      if (base !== null) return [{ periodDays, base, withSignals }]
      return withSignals === null ? [] : [{ periodDays, base, withSignals }]
    })

/**
 * Groups `plans/` into tiers and periods. Hidden: anything `isPurchasable`
 * refuses and any tier this build cannot name. Two plans for the same tier,
 * period and variant (dev has none today) resolve to the first in the
 * server's own order.
 */
export function buildCatalog(plans: readonly Plan[]): Catalog {
  const cells: Record<KnownTier, Map<number, Cell>> = {
    free: new Map(),
    bull: new Map(),
    shark: new Map(),
  }
  for (const plan of [...plans].sort(byServerOrder)) {
    const tier = knownTierSchema.safeParse(plan.tier)
    if (!tier.success || !isPurchasable(plan)) continue
    const cell = cells[tier.data].get(plan.periodDays) ?? { base: null, withSignals: null }
    if (plan.allowSignal) cell.withSignals ??= plan
    else cell.base ??= plan
    cells[tier.data].set(plan.periodDays, cell)
  }

  const periods = new Set(KNOWN_TIERS.flatMap((tier) => [...cells[tier].keys()]))
  return {
    tiers: { free: toOffers(cells.free), bull: toOffers(cells.bull), shark: toOffers(cells.shark) },
    periods: [...periods].sort((a, b) => a - b),
  }
}

export const offerFor = (
  catalog: Catalog,
  tier: KnownTier,
  periodDays: number,
): PeriodOffer | null =>
  catalog.tiers[tier].find((offer) => offer.periodDays === periodDays) ?? null

/**
 * The period every picker opens on: the longest one EVERY paid tier sells, so
 * no card opens on "not sold". On dev that is 3 months — Sadasya has no
 * yearly plan. The shortest when the tiers share none.
 */
export function defaultPeriod(catalog: Catalog): number | null {
  const selling = (['bull', 'shark'] as const)
    .map((tier) => catalog.tiers[tier])
    .filter((offers) => offers.length > 0)
  const shared = catalog.periods.filter((periodDays) =>
    selling.every((offers) => offers.some((offer) => offer.periodDays === periodDays)),
  )
  return shared.at(-1) ?? catalog.periods[0] ?? null
}

export const findPlan = (plans: readonly Plan[], planId: number): Plan | null =>
  plans.find((plan) => plan.id === planId) ?? null

const YEARLY_DAYS = 365

/** The period switch's label. */
export function periodLabel(periodDays: number): string {
  if (periodDays === 30) return 'Monthly'
  if (periodDays === 90) return '3 months'
  if (periodDays === YEARLY_DAYS) return 'Yearly'
  return `${String(periodDays)} days`
}

/** The run after a price: "Rs 2,490 a year". */
export function periodPhrase(periodDays: number): string {
  if (periodDays === 30) return 'a month'
  if (periodDays === YEARLY_DAYS) return 'a year'
  const months = monthsIn(periodDays)
  return months === null ? `for ${String(periodDays)} days` : `every ${String(months)} months`
}

/**
 * Whole months in a period: 30-day multiples, and a year as 12 — Figma's own
 * sums ("Rs 2,490 a year, Rs 208 a month"; "Yearly saves Rs 1,098" against
 * Rs 299 × 12). Null for anything else (a 3-day trial), which then shows no
 * per-month figure rather than an approximation.
 */
export function monthsIn(periodDays: number): number | null {
  if (periodDays === 365 || periodDays === 366) return 12
  return periodDays > 0 && periodDays % 30 === 0 ? periodDays / 30 : null
}

/** "Rs 208 a month" for a yearly Rs 2,490 — 2 dp; display it `{ whole: true }`. */
export function perMonth(plan: Plan): Money | null {
  const months = monthsIn(plan.periodDays)
  return months === null ? null : quantise(div(plan.price.total, months))
}

/**
 * What `plan` saves over buying `monthly` for the same span, or null — when
 * either side is missing, `monthly` is not a 30-day plan, `plan` is not a
 * whole number of months longer, or the saving is not above zero. Never an
 * estimate.
 */
export function savingOverMonthly(plan: Plan | null, monthly: Plan | null): Money | null {
  if (plan === null || monthly === null || monthly.periodDays !== 30) return null
  const months = monthsIn(plan.periodDays)
  if (months === null || months <= 1) return null
  const saving = quantise(sub(mul(monthly.price.total, months), plan.price.total))
  return sign(saving) > 0 ? saving : null
}

/**
 * The tier's lowest per-month price, for "Sadasya from Rs 208 a month".
 * Null when nothing in the tier has a per-month figure.
 */
export function cheapestPlan(
  catalog: Catalog,
  tier: KnownTier,
): { plan: Plan; perMonth: Money } | null {
  const priced = catalog.tiers[tier]
    .flatMap((offer) => [offer.base, offer.withSignals])
    .flatMap((plan) => {
      const monthly = plan === null ? null : perMonth(plan)
      return plan === null || monthly === null || isZero(monthly)
        ? []
        : [{ plan, perMonth: monthly }]
    })
  priced.sort(
    (a, b) =>
      cmp(a.perMonth, b.perMonth) ||
      cmp(a.plan.price.total, b.plan.price.total) ||
      a.plan.id - b.plan.id,
  )
  return priced[0] ?? null
}
